import React, { useEffect, useState, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'

import './style.css'
import './styles/brand-2026.css'
import './styles/legacy-adel26.css'
import SiteChat from './components/site-chat'

// Lazy loading для оптимизации - загружаем компоненты только когда нужны
const Home = lazy(() => import('./views/home'))
const NotFound = lazy(() => import('./views/not-found'))
const DigitalLeadGeneration = lazy(() => import('./views/digital-lead-generation.js'))
const B2BLeads = lazy(() => import('./views/b2b-leads.js'))
const SearchAds = lazy(() => import('./views/search-ads.js'))
const PaidSocial = lazy(() => import('./views/paid-social.js'))
const TechnicalSEO = lazy(() => import('./views/technical-seo'))
const LocalSEO = lazy(() => import('./views/local-seo'))
const SEOAudit = lazy(() => import('./views/seo-audit'))
const CorporateWebsites = lazy(() => import('./views/corporate-websites'))
const SaaSWebProducts = lazy(() => import('./views/saas-web-products'))
const Ecommerce = lazy(() => import('./views/ecommerce'))
const IOSAndroid = lazy(() => import('./views/ios-android'))
const FlutterReactNative = lazy(() => import('./views/flutter-react-native'))
const MVPScale = lazy(() => import('./views/mvp-scale'))
const CRMAnalyticsDesign = lazy(() => import('./views/crm-analytics-design'))
const CRMIntegrations = lazy(() => import('./views/crm-integrations'))
const CRMSupport = lazy(() => import('./views/crm-support'))
const CRMImplementations = lazy(() => import('./views/crm-implementations'))
const CallCenter = lazy(() => import('./views/call-center'))
const AllServices = lazy(() => import('./views/all-services'))
const Cases = lazy(() => import('./views/cases'))
const CaseStudy = lazy(() => import('./views/case-study'))
const Industries = lazy(() => import('./views/industries'))
const IndustryPage = lazy(() => import('./views/industry'))
const Locations = lazy(() => import('./views/locations'))
const MarketPage = lazy(() => import('./views/market'))
const LocationPage = lazy(() => import('./views/location'))
const Company = lazy(() => import('./views/company'))
const Partners = lazy(() => import('./views/partners'))
const PartnerPage = lazy(() => import('./views/partner'))
const Contact = lazy(() => import('./views/contact'))
const AiOs = lazy(() => import('./views/ai-os'))
const Pricing = lazy(() => import('./views/pricing'))
const Custom = lazy(() => import('./views/custom'))
const BookDemo = lazy(() => import('./views/book-demo'))
const Estimate = lazy(() => import('./views/estimate'))
const KitchensCabinets = lazy(() => import('./views/kitchens-cabinets'))
const Solutions = lazy(() => import('./views/solutions'))
const Solution = lazy(() => import('./views/solution'))
const Resources = lazy(() => import('./views/resources'))
const Faq = lazy(() => import('./views/faq'))
const ServicePage = lazy(() => import('./views/service-page'))
const Blog = lazy(() => import('./views/blog'))
const BlogPost = lazy(() => import('./views/blog-post'))
const ThankYou = lazy(() => import('./views/thank-you'))
const PrivacyPolicy = lazy(() => import('./views/privacy-policy'))
const Terms = lazy(() => import('./views/terms'))
const AuditTerms = lazy(() => import('./views/audit-terms'))

// Оптимизированный компонент загрузки
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontSize: '16px',
    color: '#b8baba',
    fontFamily: 'Manrope, Arial, sans-serif',
    background: '#202121'
  }}>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(248,248,245,0.12)',
        borderTop: '3px solid #fa704e',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
)

const AdelMotion = () => {
  const location = useLocation()

  useEffect(() => {
    const root = document.querySelector('.adel26')
    if (!root) return undefined
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const nodes = [
      ...root.querySelectorAll(
        '.adel26-pain article, .adel26-signals article, .adel26-caption, .adel26-how-row, .adel26-os-frame, .adel26-case-grid article, .adel26-industry article, .adel26-verticals a, .adel26-sol-rows a, .adel26-sol-media a, .adel26-niche-tile, .adel26-help-card, .adel26-mod-cards a, .adel26-omit article, .adel26-svc-gap article, .adel26-svc-scene article, .adel26-svc-scene a, .adel26-svc-can-list article, .adel26-addon-grid a, .adel26-split > *, .adel26-os-buy > *, .adel26-svc-cost-card > *, .ci-about > *, .ci-pain article, .ci-impl article, .ci-results article, .ci-flow > *, .ci-how-step, .ci-bubble, .ci-sees, .rt, .rt-msg, .cases-audio-item, .adel26-fit article, .adel26-delivery article, .adel26-plans article, .adel26-blog-card, .adel26-faq details, .adel26-h2, .adel26-lede, .adel26-quiz-home > *, .adel26-request > *, .adel26-svc-index > *, .adel26-contact-grid > *, .adel26-cases article, .legacy26 [class*="-feature"], .legacy26 [class*="-proof"], .legacy26 [class*="-step"], .legacy26 [class*="-index-card"], .legacy26 [class*="-challenge"]'
      ),
    ]
    const groups = new Map()
    nodes.forEach((el) => {
      const p = el.parentElement
      if (!groups.has(p)) groups.set(p, [])
      groups.get(p).push(el)
    })
    groups.forEach((els) => {
      els.forEach((el, i) => {
        el.classList.add('adel26-in')
        el.style.setProperty('--adel-in-delay', `${Math.min(i, 7) * 70}ms`)
      })
    })
    if (reduce) {
      nodes.forEach((el) => el.classList.add('is-in'))
      return undefined
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('is-in')
          io.unobserve(entry.target)
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    )
    nodes.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [location.pathname])

  return null
}

// Component to handle scroll to anchor on route change
const ScrollToAnchor = () => {
  const location = useLocation()

  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.substring(1)
      setTimeout(() => {
        const el = document.getElementById(hash)
        if (el) {
          const offset = 80 // Offset for fixed header
          const elementPosition = el.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      }, 100)
    } else {
      // Scroll to top when no hash
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [location])

  return null
}

const App = () => {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark')

  useEffect(() => {
    localStorage.setItem('theme', theme)
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  return (
    <Router>
      <ScrollToAnchor />
      <AdelMotion />
      <main id="main-content">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/services/lead-generation/digital" element={<DigitalLeadGeneration theme={theme} setTheme={setTheme} />} />
          <Route path="/services/lead-generation/b2b" element={<B2BLeads theme={theme} setTheme={setTheme} />} />
          <Route path="/services/advertising/search" element={<SearchAds theme={theme} setTheme={setTheme} />} />
          <Route path="/services/advertising/social" element={<PaidSocial theme={theme} setTheme={setTheme} />} />
          <Route path="/services/seo/technical" element={<TechnicalSEO theme={theme} setTheme={setTheme} />} />
          <Route path="/services/seo/local" element={<LocalSEO theme={theme} setTheme={setTheme} />} />
          <Route path="/services/seo/audit" element={<SEOAudit theme={theme} setTheme={setTheme} />} />
          <Route path="/services/web-development/corporate" element={<CorporateWebsites theme={theme} setTheme={setTheme} />} />
          <Route path="/services/web-development/saas" element={<SaaSWebProducts theme={theme} setTheme={setTheme} />} />
          <Route path="/services/web-development/ecommerce" element={<Ecommerce theme={theme} setTheme={setTheme} />} />
          <Route path="/services/mobile-app-development/ios-android" element={<IOSAndroid theme={theme} setTheme={setTheme} />} />
          <Route path="/services/mobile-app-development/flutter-react-native" element={<FlutterReactNative theme={theme} setTheme={setTheme} />} />
          <Route path="/services/mobile-app-development/mvp-scale" element={<MVPScale theme={theme} setTheme={setTheme} />} />
          <Route path="/services/crm-erp/analytics" element={<CRMAnalyticsDesign theme={theme} setTheme={setTheme} />} />
          <Route path="/services/crm-erp/integrations" element={<CRMIntegrations theme={theme} setTheme={setTheme} />} />
          <Route path="/services/crm-erp/support" element={<CRMSupport theme={theme} setTheme={setTheme} />} />
          <Route path="/services/crm-erp/implementations" element={<CRMImplementations theme={theme} setTheme={setTheme} />} />
          <Route path="/services/call-center" element={<CallCenter theme={theme} setTheme={setTheme} />} />
          <Route path="/services/ai-automation" element={<AiOs theme={theme} setTheme={setTheme} />} />
          <Route path="/ai-os" element={<AiOs theme={theme} setTheme={setTheme} />} />
          <Route path="/pricing" element={<Pricing theme={theme} setTheme={setTheme} />} />
          <Route path="/custom" element={<Custom theme={theme} setTheme={setTheme} />} />
          <Route path="/solutions/:slug" element={<Solution theme={theme} setTheme={setTheme} />} />
          <Route path="/solutions" element={<Solutions theme={theme} setTheme={setTheme} />} />
          <Route path="/resources" element={<Resources theme={theme} setTheme={setTheme} />} />
          <Route path="/faq" element={<Faq theme={theme} setTheme={setTheme} />} />
          <Route path="/book-demo" element={<BookDemo theme={theme} setTheme={setTheme} />} />
          <Route path="/demo" element={<BookDemo theme={theme} setTheme={setTheme} />} />
          <Route path="/estimate" element={<Estimate theme={theme} setTheme={setTheme} />} />
          <Route path="/workflow-automation" element={<Custom theme={theme} setTheme={setTheme} />} />
          <Route path="/about" element={<Navigate to="/company" replace />} />
          <Route path="/services/ads" element={<ServicePage slug="ads" theme={theme} setTheme={setTheme} />} />
          <Route path="/services/seo" element={<ServicePage slug="seo" theme={theme} setTheme={setTheme} />} />
          <Route path="/services/web-apps" element={<ServicePage slug="web-apps" theme={theme} setTheme={setTheme} />} />
          <Route path="/services" element={<AllServices theme={theme} setTheme={setTheme} />} />
          <Route path="/cases/:id" element={<CaseStudy theme={theme} setTheme={setTheme} />} />
          <Route path="/cases" element={<Cases theme={theme} setTheme={setTheme} />} />
          <Route path="/industries/kitchens-cabinets" element={<KitchensCabinets theme={theme} setTheme={setTheme} />} />
          <Route path="/industries/:slug" element={<IndustryPage theme={theme} setTheme={setTheme} />} />
          <Route path="/industries" element={<Industries theme={theme} setTheme={setTheme} />} />
          <Route path="/locations/:stateSlug/:industrySlug" element={<MarketPage theme={theme} setTheme={setTheme} />} />
          <Route path="/locations/:slug" element={<LocationPage theme={theme} setTheme={setTheme} />} />
          <Route path="/locations" element={<Locations theme={theme} setTheme={setTheme} />} />
          <Route path="/company" element={<Company theme={theme} setTheme={setTheme} />} />
          <Route path="/partners" element={<Partners theme={theme} setTheme={setTheme} />} />
          <Route path="/partners/:slug" element={<PartnerPage theme={theme} setTheme={setTheme} />} />
          <Route path="/contact" element={<Contact theme={theme} setTheme={setTheme} />} />
          <Route path="/blog" element={<Blog theme={theme} setTheme={setTheme} />} />
          <Route path="/blog/:slug" element={<BlogPost theme={theme} setTheme={setTheme} />} />
          <Route path="/thank-you" element={<ThankYou theme={theme} setTheme={setTheme} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy theme={theme} setTheme={setTheme} />} />
          <Route path="/terms" element={<Terms theme={theme} setTheme={setTheme} />} />
          <Route path="/audit-terms" element={<AuditTerms theme={theme} setTheme={setTheme} />} />
          <Route path="/" element={<Home theme={theme} setTheme={setTheme} />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </Suspense>
      </main>
      <SiteChat />
    </Router>
  )
}

// React 18 - новый способ рендеринга
const container = document.getElementById('app')
const root = createRoot(container)
root.render(<App />)
